<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  $selected-gray: #585a66;

  .audio-device-toggle {
    align-items: center;
    background: transparent;
    border: none;
    color: colors.get(textWhite);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(4px);
    padding: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    user-select: none;

    &:disabled {
      cursor: default;
      opacity: 0.5;
      pointer-events: none;
    }

    &:hover:not(:disabled) {
      opacity: 0.8;
    }

    &:active:not(:disabled) {
      transform: translateY(-50%) scale(0.95);
    }
  }

  .device-dropdown {
    background-color: rgba(40, 42, 48, 0.95);
    backdrop-filter: blur(16px);
    border: utils.in_rem(1px) solid rgba(255, 255, 255, 0.1);
    border-radius: utils.in_rem(8px);
    box-shadow: 0 utils.in_rem(4px) utils.in_rem(12px) rgba(0, 0, 0, 0.25);
    max-height: utils.in_rem(280px);
    min-width: utils.in_rem(240px);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: utils.in_rem(8px);

    .device-group {
      &:not(:last-child) {
        border-bottom: utils.in_rem(1px) solid rgba(255, 255, 255, 0.1);
        margin-bottom: utils.in_rem(8px);
        padding-bottom: utils.in_rem(8px);
      }

      .group-title {
        height: utils.in_rem(40px);
        align-items: center;
        color: colors.get(textWhite);
        display: flex;
        padding: 0 utils.in_rem(4px);
        @include mixins.body2-bold();
      }

      .device-option {
        width: 100%;
        height: utils.in_rem(40px);
        align-items: center;
        background-color: transparent;
        border: none;
        border-radius: utils.in_rem(8px);
        color: colors.get(textWhite);
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        padding: 0 utils.in_rem(12px);
        text-align: left;
        transition: background-color 0.15s ease;
        @include mixins.body2();

        .device-label {
          flex: 1;
          overflow: hidden;
          padding-right: utils.in_rem(8px);
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .check-icon {
          width: utils.in_rem(20px);
          height: utils.in_rem(20px);
          align-items: center;
          background-color: $selected-gray;
          border: utils.in_rem(2px) solid $selected-gray;
          border-radius: utils.in_rem(3px);
          display: none;
          flex-shrink: 0;
          justify-content: center;
        }

        &:hover:not(:disabled):not(.selected) {
          background-color: rgba(255, 255, 255, 0.05);
        }

        &.selected {
          background-color: $selected-gray;

          .check-icon {
            background-color: $selected-gray;
            border-color: $selected-gray;
            display: flex;
          }
        }

        &:disabled,
        &.disabled {
          cursor: default;
          opacity: 0.5;
          pointer-events: none;
        }
      }
    }
  }
</style>

<script>
  import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
    size,
  } from "@floating-ui/dom";
  import Icon from "../../../../../../svelte-design-system/components/atoms/Icon.svelte";
  import { showToast } from "../../../../../assets/js/src/general/utils";
  import {
    makeEscapeAction,
    makeWindowClick,
  } from "../../../../utils/escapable";
  import { diallerState } from "../diallerState.svelte";
  import { twilioManager } from "../utils/TwilioManager";

  const availableDevices = $derived(diallerState.availableDevices);
  const selectedInputDevice = $derived(diallerState.selectedInputDevice);
  const selectedOutputDevice = $derived(diallerState.selectedOutputDevice);

  let showDeviceDropdown = $state(false);
  let isChangingDevice = $state(false);
  let toggleEl = $state(null);
  let dropdownEl = $state(null);

  const toggleDeviceDropdown = () => {
    showDeviceDropdown = !showDeviceDropdown;
  };

  const closeDropdown = () => {
    showDeviceDropdown = false;
  };

  const handleDeviceSelect = async (type, deviceId) => {
    if (isChangingDevice) return;

    const currentDevice =
      type === "input" ? selectedInputDevice : selectedOutputDevice;
    if (deviceId === currentDevice) {
      closeDropdown();
      return;
    }

    isChangingDevice = true;
    try {
      await (type === "input"
        ? twilioManager.setInputDevice(deviceId)
        : twilioManager.setOutputDevice(deviceId));
      closeDropdown();
    } catch (error) {
      showToast({
        title: `Failed to change ${type === "input" ? "microphone" : "speaker"}`,
        subtitle: error.message || "Please try again",
        type: "danger",
      });
    } finally {
      isChangingDevice = false;
    }
  };

  const windowClick = $derived.by(() =>
    makeWindowClick(closeDropdown, toggleEl, dropdownEl)
  );

  const escape = makeEscapeAction(closeDropdown);

  const middleware = [
    offset(8),
    flip({ padding: 8 }),
    shift({ padding: 8 }),
    size({
      padding: 8,
      apply: ({ availableWidth, availableHeight, elements }) => {
        Object.assign(elements.floating.style, {
          maxWidth: `${Math.max(240, Math.min(400, availableWidth))}px`,
          maxHeight: `${Math.max(200, availableHeight)}px`,
        });
      },
    }),
  ];

  const positionDropdown = (node) => {
    if (!toggleEl) return;

    const setPosition = () =>
      computePosition(toggleEl, node, {
        placement: "bottom-end",
        middleware,
        strategy: "fixed",
      }).then(({ x, y }) => {
        node.style.position = "fixed";
        node.style.top = `${y}px`;
        node.style.left = `${x}px`;
      });

    setPosition();

    const update = () => {
      if (!(toggleEl && node)) return;
      requestAnimationFrame(setPosition);
    };

    const cleanup = autoUpdate(toggleEl, node, update);

    return {
      destroy: cleanup,
    };
  };
</script>

<svelte:window onclick={windowClick} />

<button
  bind:this={toggleEl}
  class="audio-device-toggle"
  onclick={toggleDeviceDropdown}
  disabled={isChangingDevice}
  data-testid="dialler-device-selector"
  title="Select audio devices"
  type="button"
  aria-label="Select audio devices"
>
  <Icon class="fa-solid fa-chevron-down" size="xxxs" />
</button>

{#if showDeviceDropdown}
  <div
    bind:this={dropdownEl}
    class="device-dropdown"
    use:positionDropdown
    use:escape
  >
    <div class="device-group">
      <div class="group-title">Select a microphone</div>
      {#each availableDevices?.inputs || [] as device}
        <button
          class="device-option"
          class:selected={device.deviceId === selectedInputDevice}
          onclick={() => handleDeviceSelect("input", device.deviceId)}
          disabled={isChangingDevice}
          type="button"
        >
          <span class="device-label">
            {device.label || `Microphone ${device.deviceId.slice(-4)}`}
          </span>
          <span class="check-icon">
            <Icon class="fa-solid fa-check" size="xs" />
          </span>
        </button>
      {/each}
      {#if !availableDevices?.inputs || availableDevices.inputs.length === 0}
        <button class="device-option disabled" disabled type="button">
          <span class="device-label">No microphones available</span>
        </button>
      {/if}
    </div>

    <div class="device-group">
      <div class="group-title">Select a speaker</div>
      {#each availableDevices?.outputs || [] as device}
        <button
          class="device-option"
          class:selected={device.deviceId === selectedOutputDevice}
          onclick={() => handleDeviceSelect("output", device.deviceId)}
          disabled={isChangingDevice}
          type="button"
        >
          <span class="device-label">
            {device.label || `Speaker ${device.deviceId.slice(-4)}`}
          </span>
          <span class="check-icon">
            <Icon class="fa-solid fa-check" size="xs" />
          </span>
        </button>
      {/each}
      {#if !availableDevices?.outputs || availableDevices.outputs.length === 0}
        <button class="device-option disabled" disabled type="button">
          <span class="device-label">No speakers available</span>
        </button>
      {/if}
    </div>
  </div>
{/if}
